import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// TODO: this needs to be moved to the portal lib
import { cloneElement, isValidElement, useCallback, useContext, useEffect, useState, } from 'react';
import { useRecoilValue, useSetRecoilState, } from 'recoil';
import { RefsContext, StepValidationContext, useGetRef, useStepValidation, } from '@zurich/common/contexts';
import { findTopElement } from '@zurich/common/forms';
import { useScrollIntoView, useTranslation } from '@zurich/common/hooks';
import { alwaysValidValidationSelector } from '@zurich/common/recoil';
import { Stack } from '../../../atoms';
import { useIsMobile } from '../../../hooks';
import { Step, StepNavigation, Stepper, } from '../../components/Stepper';
import { scrollToFieldAtom } from '../../recoil/dialog';
import { styled } from '../../utils';
const DialogStepperStickyContentStyled = styled('div')(({ theme }) => ({
    overflowY: 'auto',
    padding: '2px',
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        paddingBottom: theme.spacing(1),
    },
}));
// TODO: find a solution for a Storybook story for this component (currently complicated because of Recoil values)
export function DialogStepper({ steps, stepnavigationNextLabel, stepnavigationPrevLabel, stepnavigationLastLabel, onSave, onClose, loading, contentRef, buttonPosition, sticky = true, onStepChange, scrollToError = true, oneStepHideCancelButton = false, }) {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const { stepValidation, triggerStepValidation, resetStepValidation } = useStepValidation();
    // get validation for current step
    const validatorMap = steps.map(({ validationSelector }) => validationSelector);
    const activeStepValidation = useRecoilValue(
    // if no validationSelector is provided, disable validation
    validatorMap[activeStep] || alwaysValidValidationSelector);
    // scroll to error
    const setScrollTo = useSetRecoilState(scrollToFieldAtom);
    const { refs } = useContext(RefsContext);
    const fieldName = useRecoilValue(scrollToFieldAtom);
    const ref = useGetRef(fieldName);
    const scrollTo = useScrollIntoView(ref);
    const isMobile = useIsMobile();
    const scrollToErrorLocal = useCallback(() => {
        if (!scrollToError || Array.isArray(activeStepValidation.errors)) {
            return;
        }
        const [topElement] = findTopElement(refs, activeStepValidation.errors, 0);
        if (topElement) {
            setScrollTo(topElement);
        }
        return topElement;
    }, [scrollToError, refs, activeStepValidation.errors, setScrollTo]);
    // actually scroll to field if triggered by fieldName
    useEffect(() => {
        if (!scrollToError) {
            return;
        }
        // TODO add check if jump to a later page for DataEntryDialog Sidebar!!!
        if (fieldName !== null && stepValidation > 0)
            scrollTo();
    }, [fieldName, scrollTo, scrollToError, stepValidation]);
    useEffect(() => {
        if (!scrollToError) {
            return;
        }
        contentRef?.current?.scrollTo({
            behavior: 'smooth',
            top: 0,
        });
    }, [activeStep, contentRef, scrollToError]);
    const handleNext = async () => {
        if (activeStepValidation?.valid === true) {
            if (steps?.[activeStep]?.onNext) {
                await steps[activeStep].onNext();
            }
            if (activeStep < steps.length - 1) {
                setActiveStep((step) => step + 1);
                resetStepValidation();
            }
            // last step should trigger the save action if valid!
            if (activeStep === steps.length - 1) {
                if (onSave)
                    onSave();
            }
        }
        else {
            triggerStepValidation();
            scrollToErrorLocal();
        }
    };
    const handlePrev = () => {
        if (activeStep > 0) {
            setActiveStep((step) => step - 1);
            resetStepValidation();
        }
    };
    const handleCancel = () => {
        if (onClose)
            onClose();
    };
    useEffect(() => {
        onStepChange?.(activeStep);
    }, [activeStep, onStepChange]);
    const stepperContentProps = {
        loading,
    };
    // if only one step is provided, show without stepper
    if (steps?.length === 1) {
        const step = steps[0];
        return (_jsx(StepValidationContext.Provider, { value: stepValidation, children: _jsxs(Stack, { children: [_jsx(DialogStepperStickyContentStyled, { children: isValidElement(step.content) &&
                            cloneElement(step.content, stepperContentProps) }), _jsx(StepNavigation, { labelNext: stepnavigationNextLabel ?? t('common:button_label.save'), labelPrev: stepnavigationPrevLabel ?? t('common:button_label.cancel'), labelLast: stepnavigationLastLabel, onClickNext: handleNext, onClickPrev: handleCancel, sticky: sticky, loading: loading, loadingNext: loading, disabledNext: step.disabledNext, disabledNextReason: step.disabledNextReason, buttonPosition: buttonPosition, hideCancelButton: oneStepHideCancelButton, iconPrevButton: "arrow-return-1", iconNextButton: "check" })] }) }));
    }
    return (_jsx(StepValidationContext.Provider, { value: stepValidation, children: _jsx(Stepper, { activeStep: activeStep, alternativeLabel: true, orientation: "horizontal", stickyNavigation: sticky, contentTitle: isMobile, children: steps.map((step, index) => (_jsx(Step, { label: step.title, title: step.title, subtitle: step.subtitle, first: index === 0, onClickNext: handleNext, onClickPrev: handlePrev, labelNext: index === steps.length - 1
                    ? (stepnavigationLastLabel ?? t('common:button_label.save'))
                    : (stepnavigationNextLabel ?? t('common:button_label.next')), loading: loading, loadingNext: loading, disabledNext: step.disabled, iconNextButton: index === steps.length - 1 ? 'check' : undefined, children: isValidElement(step.content) &&
                    cloneElement(step.content, stepperContentProps) }, index))) }) }));
}
